/** Mirrors the backend ProformaDtos. One file, so a contract change lands once. */

export type ProformaStatus =
  | 'DRAFT'
  | 'PENDING_APPROVAL'
  | 'RETURNED'
  | 'REJECTED'
  | 'APPROVED'
  | 'CANCELLED'
  | 'POSTED'
  | 'PARTIALLY_POSTED'
  | 'POST_FAILED'
  | 'INVOICED'
  | 'CLOSED'

/**
 * Follows the task: KONTRAK_PO or QUOTATION, by the kind of agreement Oracle
 * holds for it. The other two only appear on documents written before
 * October 2026.
 */
export type IssuanceBasis = 'KONTRAK_PO' | 'QUOTATION' | 'BERITA_ACARA' | 'LAINNYA'

export interface LineRequest {
  serviceCode: string
  itemCode?: string | null
  description: string
  uom?: string | null
  quantity: number
  unitPrice: number
  discountPct?: number | null
  discountAmt?: number | null
  taxCode?: string | null
  whtCode?: string | null
  costCenter?: string | null
  glAccount?: string | null
  remarks?: string | null
}

/**
 * A draft as the form sends it. Most of the header is chosen, not typed: the
 * form names a customer site, a project task, a project manager and a
 * receiving account, and the server copies their details from the masters.
 * The document date and VA number are set by the server on submit.
 */
export interface SaveRequest {
  branchId: string

  customerCode: string
  customerSiteId?: number | null
  /** Starts as the master's name; may be changed for this document. */
  customerName: string
  clientPicName?: string | null
  clientEmail?: string | null

  projectCode?: string | null
  projectTaskNo?: string | null
  projectManagerId?: string | null

  bastNo?: string | null
  bastDate?: string | null

  currency: string
  wapu: boolean
  bankSiho: boolean
  receivingAccountId?: number | null
  virtualAccount: boolean
  appliesToPad?: string | null

  referenceNo?: string | null
  description?: string | null

  /** Present on update only; the optimistic lock. */
  version?: number | null

  lines: LineRequest[]
}

export interface LineResponse extends LineRequest {
  id: number
  lineNo: number
  taxRate: number
  taxAmt: number
  whtRate: number
  whtAmt: number
  lineAmt: number
}

export interface ProformaResponse extends Omit<SaveRequest, 'lines' | 'version'> {
  id: number
  docNo: string
  /**
   * True while docNo is the draft's stand-in (DRAFT-000412) and docDate only
   * the day it was last saved. Both become final on first submit.
   */
  provisionalNumber: boolean
  status: ProformaStatus
  docDate: string
  dueDate?: string | null
  customerSiteNumber?: string | null
  customerNitku?: string | null
  customerNpwp?: string | null
  customerAddress?: string | null
  customerEntityType?: string | null
  projectName?: string | null
  projectTaskName?: string | null
  projectManagerName?: string | null
  issuanceBasis?: IssuanceBasis | null
  quotationNo?: string | null
  quotationDate?: string | null
  contractNo?: string | null
  contractDate?: string | null
  contractAmt?: number | null
  bankName?: string | null
  bankAccountNo?: string | null
  bankAccountName?: string | null
  vaNumber?: string | null
  taxInvoiceNo?: string | null
  createdAt?: string | null
  updatedAt?: string | null
  subtotalAmt: number
  discountAmt: number
  taxAmt: number
  withholdingAmt: number
  totalAmt: number
  /** What the customer transfers. Differs from totalAmt only under WAPU. */
  amountDue: number
  version: number
  /** Who raised it. Decides whether submit and withdraw are offered. */
  drafterUserId: number | null
  lines: LineResponse[]
}

export interface ProformaSummary {
  id: number
  docNo: string
  status: ProformaStatus
  docDate: string
  customerName: string
  projectCode: string | null
  currency: string
  totalAmt: number
  navPostStatus: string
  oraclePostStatus: string
  /** The document number each ERP gave it; null until posted there. */
  navDocNo?: string | null
  oracleDocNo?: string | null
}

export interface CodeRate {
  code: string
  rate: number
  description: string | null
}

export interface Lookups {
  taxCodes: CodeRate[]
  whtCodes: CodeRate[]
  currencies: Array<{ code: string; scale: number }>
}

export interface PageResponse<T> {
  content: T[]
  page: number
  size: number
  totalElements: number
  totalPages: number
  first: boolean
  last: boolean
}

export const STATUS_LABEL: Record<ProformaStatus, string> = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Menunggu Persetujuan',
  RETURNED: 'Dikembalikan',
  REJECTED: 'Ditolak',
  APPROVED: 'Disetujui',
  CANCELLED: 'Dibatalkan',
  POSTED: 'Terkirim',
  PARTIALLY_POSTED: 'Terkirim Sebagian',
  POST_FAILED: 'Gagal Terkirim',
  INVOICED: 'Sudah Ditagihkan',
  CLOSED: 'Selesai',
}

export const BASIS_LABEL: Record<IssuanceBasis, string> = {
  KONTRAK_PO: 'Kontrak',
  QUOTATION: 'Quotation',
  BERITA_ACARA: 'Berita Acara',
  LAINNYA: 'Lainnya',
}

// --- the form's pickers (ProformaLookupDtos) ------------------------------

export interface BranchOption {
  code: string
  name: string
}

export interface Employee {
  /** NIP in the sample data; Oracle's person_id once projects come from Oracle. */
  id: string
  name: string
  unit: string
}

export interface FormContext {
  /** The caller's own branches, with names. */
  branches: BranchOption[]
  projectManagers: Employee[]
  /** True while projects, tasks and managers are sample data, not Oracle. */
  projectsSample: boolean
  /** The same, for bank accounts. */
  accountsSample: boolean
}

export interface SiteOption {
  /** SITE_USE_ID */
  id: number
  siteNumber: string | null
  label: string
  operatingUnit: string | null
  city: string | null
  address: string | null
  nitku: string | null
  primary: boolean
}

/** A customer as the search lists it: sites only counted, fetched once it is picked. */
export interface CustomerHit {
  code: string
  name: string
  entityType: string | null
  npwp: string | null
  email: string | null
  /**
   * Active BILL_TO sites the issuing branch may bill. Missing until the
   * customer's sites at the branch's unit have been taken from Oracle, which
   * happens when it is picked.
   */
  siteCount?: number | null
  /** The site when there is exactly one. */
  onlySiteId: number | null
}

export interface CustomerOption {
  code: string
  name: string
  entityType: string | null
  npwp: string | null
  email: string | null
  /**
   * The issuing branch's Oracle unit when only its sites are listed; null when
   * the branch is not mapped to a unit yet and every unit's sites are listed.
   */
  billingUnit: string | null
  /** BILL_TO sites only, primary first. */
  sites: SiteOption[]
  /** When the sites were taken from Oracle; missing when they never were one customer at a time. */
  sitesSyncedAt?: string | null
  /**
   * Why the sites could not be taken from Oracle just now, so the ones listed
   * may be old or missing. Missing when nothing went wrong.
   */
  sitesProblem?: string | null
}

export interface ProjectOption {
  code: string
  name: string
  organization: string
  customerNumber: string | null
  managerId: string | null
  taskCount: number
}

export interface TaskOption {
  number: string
  name: string
  /** Kontrak or Quotation, by the task's agreement in Oracle. */
  basis: 'KONTRAK_PO' | 'QUOTATION'
  /** The agreement's number, date and value, whichever kind it is. */
  contractNo: string | null
  contractDate: string | null
  contractAmt: number | null
  /** Subtotal of the other live proformas on this task, in the asked currency. */
  billedAmt: number
}

export interface ProjectDetail extends Omit<ProjectOption, 'taskCount'> {
  tasks: TaskOption[]
}

export interface ReceivingOption {
  id: number
  bankCode: string
  bankName: string
  accountNo: string
  accountName: string
  currency: string
  defaultAccount: boolean
}
